import { api } from './api';

const prefix = 'voidhead.';
const empty = () => ({ sessions: 0, timeMeditated: 0 });
let owner = 'guest';
let user = null;
let syncing = null;
let syncingOwner = null;
const listeners = new Set();

function read(key, fallback) {
  const raw = localStorage.getItem(key);
  if (!raw) return fallback;
  try { return JSON.parse(raw); } catch { return fallback; }
}

function entries() {
  return Object.keys(localStorage).filter(key => key.startsWith(`${prefix}entry.`))
    .map(key => ({ key, ...read(key, {}) })).filter(entry => entry.owner === owner);
}

function write(entry) {
  localStorage.setItem(`${prefix}entry.${entry.id}`, JSON.stringify(entry));
}

function notify() {
  listeners.forEach(callback => callback(getStats()));
}

export function getStats() {
  const result = { ...read(`${prefix}totals.${owner}`, empty()) };
  for (const entry of entries().filter(entry => !entry.synced)) {
    result.sessions += entry.sessions ?? 1;
    result.timeMeditated += entry.timeMeditated ?? entry.duration;
  }
  return result;
}

export function preserveLegacy() {
  owner = localStorage.getItem(`${prefix}owner`) || 'guest';
  if (localStorage.getItem(`${prefix}legacy`)) return;
  const stats = read('stats_data', empty());
  const pending = read('stats_data_unsynced', empty());
  const profile = read('user_data', null);
  const id = crypto.randomUUID();
  const backup = { id, stats, pending, profile };
  localStorage.setItem(`${prefix}legacy`, JSON.stringify(backup));
  if (!profile || profile.isAnonymous) {
    write({ id, owner: 'guest', sessions: stats.sessions + pending.sessions,
      timeMeditated: stats.timeMeditated + pending.timeMeditated, synced: false });
  }
}

export function setUser(nextUser) {
  user = nextUser;
  owner = user ? String(user.id) : 'guest';
  localStorage.setItem(`${prefix}owner`, owner);
  if (user) {
    for (const key of Object.keys(localStorage).filter(key => key.startsWith(`${prefix}entry.`))) {
      const entry = read(key, null);
      if (entry?.owner === 'guest') write({ ...entry, owner });
    }
    const backup = read(`${prefix}legacy`, null);
    if (backup?.profile?.uid && !backup.profile.isAnonymous && backup.profile.uid === user.firebaseUid
      && !localStorage.getItem(`${prefix}entry.${backup.id}`)) {
      write({ id: backup.id, owner, synced: false,
        sessions: Math.max(0, backup.stats.sessions - user.legacy.sessions) + backup.pending.sessions,
        timeMeditated: Math.max(0, backup.stats.timeMeditated - user.legacy.timeMeditated) + backup.pending.timeMeditated });
    }
  }
  notify();
}

export function complete(duration) {
  const entry = { id: crypto.randomUUID(), owner, completedAt: new Date().toISOString(), duration, synced: false };
  write(entry);
  notify();
  return entry;
}

export function sync() {
  if (!user) return Promise.resolve();
  if (syncing) return syncingOwner === owner ? syncing : syncing.catch(() => {}).then(() => sync());
  const account = user.id;
  const scope = owner;
  syncingOwner = scope;
  syncing = (async () => {
    let again;
    do {
      const pending = entries().filter(entry => !entry.synced);
      const sessions = pending.filter(entry => entry.completedAt).slice(0, 500);
      const legacy = pending.filter(entry => !entry.completedAt).slice(0, 20);
      const result = await api('/progress', { userId: account, sessions, legacy });
      if (owner !== scope || user?.id !== account) return;
      const previous = read(`${prefix}totals.${scope}`, empty());
      localStorage.setItem(`${prefix}totals.${scope}`, JSON.stringify({
        sessions: Math.max(previous.sessions, result.stats.sessions),
        timeMeditated: Math.max(previous.timeMeditated, result.stats.timeMeditated),
      }));
      const accepted = new Set([...result.accepted, ...result.acceptedLegacy]);
      for (const entry of pending.filter(entry => accepted.has(entry.id))) {
        const current = read(entry.key, null);
        if (current?.owner === scope) write({ ...current, synced: true });
      }
      notify();
      again = entries().some(entry => !entry.synced);
    } while (again);
  })().finally(() => { syncing = null; });
  return syncing;
}

export const hasPending = () => entries().some(entry => !entry.synced);
export const onStatsChanged = callback => {
  listeners.add(callback);
  return () => listeners.delete(callback);
};
export const refresh = notify;
